import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CalendarPlus, Lock, Pencil, Trash2, ImagePlus, X, Ticket, MapPin } from "lucide-react";
import { api, errMsg, media } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { EventRow } from "@/components/fo/Events";

const box = "h-11 border-white/10 bg-[#07090E]";
const EMPTY = { title: "", date: "", time: "", venue: "", address: "", city: "", price: "", ticket_url: "", info: "", poster: null };
const todayStr = () => new Date().toLocaleDateString("sv-SE");

const Locked = ({ go }) => (
  <div className="panel relative overflow-hidden p-8 text-center md:p-14" data-testid="events-locked">
    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-[#FFB800]/40 bg-[#FFB800]/10"><Lock className="h-6 w-6 text-[#FFB800]" /></div>
    <h2 className="mt-5 font-display text-2xl font-black uppercase">Promote your shows</h2>
    <p className="mx-auto mt-3 max-w-md text-slate-400">Add your gigs with date, venue, address and tickets. They appear on your artist page and in the <strong className="text-white">On stage soon</strong> showcase on the home page.</p>
    <button className="btn-hot mt-6" onClick={() => go("plan")} data-testid="events-upgrade">Upgrade to add your events</button>
    <div className="pointer-events-none mt-10 space-y-3 opacity-30 blur-[1px]">
      {[0, 1].map((i) => <div key={i} className="mx-auto flex max-w-md items-center gap-4 rounded-xl border border-white/10 p-3 text-left"><div className="h-14 w-14 rounded-lg bg-[#FF2E63]/30" /><div className="flex-1 space-y-2"><div className="h-3 w-2/3 rounded bg-white/20" /><div className="h-2 w-1/2 rounded bg-white/10" /></div></div>)}
    </div>
  </div>
);

const Poster = ({ v, set }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const up = async (e) => {
    const f = e.target.files[0]; e.target.value = "";
    if (!f) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", f); set((await api.post("/me/brief-upload", fd)).data.url); } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  return v ? (
    <div className="relative h-32 w-24 overflow-hidden rounded-lg border border-white/10"><img src={media(v)} alt="" className="h-full w-full object-cover" />
      <button type="button" onClick={() => set(null)} className="absolute right-1 top-1 rounded-full bg-black/70 p-1" data-testid="event-poster-remove"><X className="h-3 w-3" /></button></div>
  ) : (
    <button type="button" onClick={() => ref.current.click()} className="flex h-32 w-24 flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-white/20 text-slate-500 hover:border-[#FF2E63] hover:text-[#FF2E63]" data-testid="event-poster-add">
      <ImagePlus className="h-5 w-5" /><span className="font-mono text-[10px] uppercase">{busy ? "…" : "Poster"}</span>
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={up} />
    </button>
  );
};

const EventForm = ({ init, onDone, onCancel }) => {
  const [v, setV] = useState({ ...EMPTY, ...init });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setV({ ...v, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      init?.id ? await api.put(`/me/events/${init.id}`, v) : await api.post("/me/events", v);
      toast.success(init?.id ? "Event updated" : "Event published", { description: "It's live on your artist page." }); onDone();
    } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="panel space-y-3 border-[#FF2E63]/30 p-5" data-testid="event-form">
      <div className="label-mono">{init?.id ? "Edit event" : "New event"}</div>
      <Input required placeholder="Event title — e.g. Live at Spazio 211" value={v.title} onChange={set("title")} className={box} data-testid="event-title" />
      <div className="grid grid-cols-2 gap-3">
        <Input required type="date" min={todayStr()} value={v.date} onChange={set("date")} className={box} data-testid="event-date" />
        <Input type="time" value={v.time} onChange={set("time")} className={box} data-testid="event-time" />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Input required placeholder="Venue" value={v.venue} onChange={set("venue")} className={box} data-testid="event-venue" />
        <Input required placeholder="City" value={v.city} onChange={set("city")} className={box} data-testid="event-city" />
      </div>
      <Input placeholder="Address (street, number)" value={v.address} onChange={set("address")} className={box} data-testid="event-address" />
      <div className="grid gap-3 sm:grid-cols-2">
        <Input placeholder="Price — e.g. €10 or Free entry" value={v.price} onChange={set("price")} className={box} data-testid="event-price" />
        <Input placeholder="Tickets link" value={v.ticket_url} onChange={set("ticket_url")} className={box} data-testid="event-tickets" />
      </div>
      <div className="flex gap-3">
        <Textarea rows={5} placeholder="Info: line-up, doors, dress code, age limit, updates…" value={v.info} onChange={set("info")} className="flex-1 border-white/10 bg-[#07090E]" data-testid="event-info" />
        <Poster v={v.poster} set={(p) => setV((x) => ({ ...x, poster: p }))} />
      </div>
      <div className="flex gap-2">
        <button className="btn-hot flex-1" disabled={busy} data-testid="event-save">{busy ? "Saving…" : init?.id ? "Save changes" : "Publish event"}</button>
        <button type="button" className="btn-ghost" onClick={onCancel} data-testid="event-cancel">Cancel</button>
      </div>
    </form>
  );
};

const MyEvent = ({ e, edit, del }) => (
  <div className={`relative ${e.past ? "opacity-50" : ""}`}>
    {e.hidden && <span className="absolute right-0 top-2 font-mono text-[10px] uppercase text-[#FF2E63]">Hidden by Fuorionda</span>}
    <EventRow e={e} />
    {!e.past && (
      <div className="-mt-3 mb-3 flex gap-2 pl-20">
        <button className="btn-ghost !px-3 !py-1 text-xs" onClick={() => edit(e)} data-testid={`event-edit-${e.id}`}><Pencil className="h-3 w-3" />Edit</button>
        <button className="btn-ghost !px-3 !py-1 text-xs hover:!text-[#FF2E63]" onClick={() => del(e)} data-testid={`event-delete-${e.id}`}><Trash2 className="h-3 w-3" />Delete</button>
      </div>
    )}
  </div>
);

export const Events = ({ d, go }) => {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(null);
  const paid = d.plan.id !== "free";
  const load = () => api.get("/me/events").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const del = async (e) => {
    if (!window.confirm(`Delete "${e.title}"?`)) return;
    await api.delete(`/me/events/${e.id}`); toast.success("Event deleted"); load();
  };
  const upcoming = list.filter((e) => !e.past), past = list.filter((e) => e.past);
  if (!paid && !list.length) return <Locked go={go} />;
  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="dash-events">
      {!paid && <div className="panel flex flex-wrap items-center justify-between gap-3 border-[#FFB800]/40 p-4 text-sm" data-testid="events-paused"><span className="flex items-center gap-2"><Lock className="h-4 w-4 text-[#FFB800]" />Your events are saved but hidden from the public until you renew your plan.</span><button className="btn-hot !px-4 !py-1.5 text-xs" onClick={() => go("plan")}>Renew</button></div>}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><div className="label-mono">Live shows</div><p className="mt-1 text-sm text-slate-400">Shown on your page and, in the 30 days before, in the home page showcase.</p></div>
        {paid && !form && <button className="btn-hot !px-4 !py-2 text-sm" onClick={() => setForm({})} data-testid="event-new"><CalendarPlus className="h-4 w-4" />Add event</button>}
      </div>
      {form && <EventForm init={form} onDone={() => { setForm(null); load(); }} onCancel={() => setForm(null)} />}
      <div className="panel px-5" data-testid="my-events-list">
        {upcoming.map((e) => <MyEvent key={e.id} e={e} edit={(x) => { setForm(x); window.scrollTo({ top: 0, behavior: "smooth" }); }} del={del} />)}
        {!upcoming.length && <div className="flex flex-col items-center gap-2 py-10 text-center text-slate-500"><Ticket className="h-6 w-6" /><p className="text-sm">No upcoming shows yet.</p></div>}
      </div>
      {past.length > 0 && <details className="panel px-5 py-3"><summary className="cursor-pointer font-mono text-xs uppercase tracking-widest text-slate-500"><MapPin className="mr-1 inline h-3 w-3" />Past shows ({past.length})</summary>{past.map((e) => <MyEvent key={e.id} e={e} />)}</details>}
    </div>
  );
};
